/**
 * 内置策略：代价函数群控（设计文档 3.4 表 / F-007，代价 J = w1·等待 + w2·绕行 + w3·满载）。
 *
 * 权重经 ParamField 描述符暴露（params），可由 F-009 参数面板实时调节（AC-F008-03 / AC-F009-01）。
 */
import { greedyAssign } from '../greedy-assign';
import { travelTimeForFloorsMs } from '../../core/physics/kinematics';
import type { DispatchStrategy, InitContext, ParamField, SnapshotView, StrategyParams } from '../strategy-api';
import type { KinematicsParams } from '../../core/physics/kinematics';

interface CostGroupParams {
  waitWeight: number;
  detourWeight: number;
  loadWeight: number;
}

const DEFAULTS: CostGroupParams = { waitWeight: 1, detourWeight: 5, loadWeight: 30 };

/** 参数描述符（F-009 表单由此生成）。 */
const PARAMS: readonly ParamField[] = [
  { key: 'waitWeight', label: '预计等待时间权重', default: DEFAULTS.waitWeight, min: 0, step: 0.5 },
  { key: 'detourWeight', label: '绕行/已承诺代价权重', default: DEFAULTS.detourWeight, min: 0, step: 1 },
  { key: 'loadWeight', label: '满载惩罚权重', default: DEFAULTS.loadWeight, min: 0, step: 1 },
];

export function createCostGroupStrategy(id = 'cost-group'): DispatchStrategy {
  let kin: KinematicsParams | undefined;
  let params: CostGroupParams = { ...DEFAULTS };
  return {
    id,
    params: PARAMS,
    init(ctx: InitContext) {
      kin = ctx.kinematics;
    },
    setParams(p: StrategyParams) {
      params = {
        waitWeight: typeof p.waitWeight === 'number' ? p.waitWeight : params.waitWeight,
        detourWeight: typeof p.detourWeight === 'number' ? p.detourWeight : params.detourWeight,
        loadWeight: typeof p.loadWeight === 'number' ? p.loadWeight : params.loadWeight,
      };
    },
    allocate(view: SnapshotView) {
      const k = kin!;
      const { waitWeight, detourWeight, loadWeight } = params;
      return greedyAssign(view.cars, view.calls, (car, call, extra) => {
        const etaSec = travelTimeForFloorsMs(Math.abs(car.floor - call.floor), k) / 1000;
        const detour = car.pending + extra; // 已承诺接客越多，绕行代价越大
        const loadRatio = car.capacity > 0 ? car.load / car.capacity : 0;
        // C：方向亲和——正反向运行的车加罚（与等待项同量纲）
        const opposite = car.state === 'moving' && car.direction !== 0 && car.direction !== call.direction;
        const dirPenalty = opposite ? 0.5 * etaSec : 0;
        return waitWeight * etaSec + detourWeight * detour + loadWeight * loadRatio + dirPenalty;
      });
    },
  };
}
